import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Answer } from "../../../assets/images/Answer";
import { Star } from "../../../assets/images/Star";
import { Book } from "../../../assets/images/Book";
import { Tab } from "../Tab/Tab";
import { TabsContainer } from "../Tab/TabsContainer";
import { LoadingIndicator } from "../LoadingIndicator/LoadingIndicator";
import { LogContentDisplay } from "../../Logs/AnswerLogs/AnswerDetails/LogContentDisplay/LogContentDisplay";
import { InfoSection } from "../../Logs/AnswerLogs/AnswerDetails/InfoSection/InfoSection";
import { SourcesSection } from "../../Logs/AnswerLogs/AnswerDetails/SourcesSection/SourcesSection";
import {
  Container,
  MainContent,
  ConversationColumn,
  SummaryColumn,
  ErrorContainer,
} from "./AnswerDetailsView.styles";

export const AnswerDetailsView = ({
  conversationComponent,
  summary = "",
  question = "",
  answer = "",
  sources = [],
  product = "",
  macrosSuggestions = null,
  selectedMacroId = null,
  selectedMacroSource = null,
  selectedMacro = null,
  logsData = [],
  isLoading = false,
  loadingMessage = "",
  error = null,
  errorInfo = null,
  successStatus = "",
  children = null,
}) => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState("getSummaryResponse");

  const stepToTabInfo = {
    getSummaryResponse: {
      name: "getSummaryResponse",
      title: t("logs.definition"),
      icon: <Answer />,
    },
    findMacrosSuggestions: {
      name: "findMacrosSuggestions",
      title: t("logs.macros"),
      icon: <Star />,
    },
    validateQuestionSimilarity: {
      name: "validateQuestionSimilarity",
      title: t("logs.validation"),
      icon: <Book />,
    },
    filterSections: {
      name: "filterSections",
      title: t("logs.search"),
      icon: <Book />,
    },
    generateAnswerFromSections: {
      name: "generateAnswerFromSections",
      title: t("logs.answer"),
      icon: <Star />,
    },
  };

  const tabs = logsData && logsData.length > 0
    ? logsData
        .map((log) => stepToTabInfo[log.step])
        .filter((tab) => tab)
    : [];

  const getCurrentLogData = () => {
    if (!logsData || !Array.isArray(logsData) || logsData.length === 0)
      return null;

    return logsData.find((log) => log.step === activeTab) || null;
  };

  const renderTabContent = () => {
    const logData = getCurrentLogData();
    return (
      <LogContentDisplay
        logData={logData}
        t={t}
        selectedMacroId={selectedMacroId}
        selectedMacroSource={selectedMacroSource}
        selectedMacro={selectedMacro}
      />
    );
  };

  if (isLoading) {
    return <LoadingIndicator message={loadingMessage || t("common.loading")} />;
  }

  if (error) {
    return <ErrorContainer>{error}</ErrorContainer>;
  }

  const getErrorMessage = () => {
    if (!errorInfo) return "";

    if (errorInfo.error_key && t(`errors.${errorInfo.error_key}`)) {
      return t(`errors.${errorInfo.error_key}`);
    }

    return errorInfo.error_text || "";
  };

  const getStatusTranslation = () => {
    if (!successStatus) return "";
    return t(`status.${successStatus}`) || successStatus;
  };

  const getSelectedMacro = () => {
    if (!selectedMacroId) {
      return null;
    }

    // First, check if we have the full macro details from the backend
    if (selectedMacro && selectedMacro.title) {
      return selectedMacro;
    }

    // Try to find the macro in suggestions array
    if (macrosSuggestions && macrosSuggestions.suggestions) {
      const macro = macrosSuggestions.suggestions.find(m => m.id === selectedMacroId);
      if (macro) {
        return macro;
      }
    }

    // If not found anywhere, return a minimal object with ID
    return { id: selectedMacroId, title: null };
  };

  const getSelectedMacroSourceLabel = () => {
    if (!selectedMacroSource) return "";
    return selectedMacroSource === "search" ? t("logs.fromSearch") : t("logs.fromSuggestion");
  };

  const getSelectedMacroDisplay = () => {
    const macro = getSelectedMacro();
    if (!macro) return "";

    if (macro.title) {
      return `${macro.title} (${getSelectedMacroSourceLabel()})`;
    }

    // If we don't have the title, show the ID
    return `${t("logs.macroId")}: ${macro.id} (${getSelectedMacroSourceLabel()})`;
  };

  return (
    <Container>
      {children}

      <MainContent>
        <ConversationColumn>{conversationComponent}</ConversationColumn>

        <SummaryColumn>
          <InfoSection
            title={t("logs.summary")}
            content={summary}
            emptyMessage={t("logs.noSummary")}
          />

          <InfoSection
            title={t("logs.question")}
            content={question}
            emptyMessage={t("logs.noQuestion")}
          />

          <InfoSection
            title={t("logs.answer")}
            content={answer}
            emptyMessage={t("logs.noAnswer")}
          />
          <SourcesSection sources={sources} t={t} />

          {product && (
            <InfoSection
              title={t("logs.product")}
              content={product}
              emptyMessage=""
            />
          )}

          {errorInfo && (
            <InfoSection
              title={t("logs.error")}
              content={getErrorMessage()}
              emptyMessage={t("logs.noError")}
            />
          )}

          {successStatus && (
            <InfoSection
              title={t("logs.status")}
              content={getStatusTranslation()}
              emptyMessage={t("logs.noStatus")}
            />
          )}

          {selectedMacroId && (
            <InfoSection
              title={t("logs.selectedMacro")}
              content={getSelectedMacroDisplay()}
              emptyMessage=""
            />
          )}
        </SummaryColumn>

        <div>
          {tabs.length > 0 ? (
            <>
              <TabsContainer>
                {tabs.map((tab) => (
                  <Tab
                    key={tab.name}
                    title={tab.title}
                    icon={tab.icon}
                    isActive={activeTab === tab.name}
                    onClick={() => setActiveTab(tab.name)}
                  />
                ))}
              </TabsContainer>
              {renderTabContent()}
            </>
          ) : (
            <InfoSection
              title={t("logs.logsData")}
              content={t("logs.noLogsAvailable")}
              emptyMessage={t("logs.noLogsAvailable")}
            />
          )}
        </div>
      </MainContent>
    </Container>
  );
};
